import { toast } from "@devhop/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { reminderReasonDescription } from "../lib/reminder-feedback";
import { cfMessageApi } from "./client";
import type { CustomerStatusEventType, TemplateKind } from "./types";

function requirePageId(pageId: string | null): string {
  if (!pageId) throw new Error("page_id_required");
  return pageId;
}

export const cfMessageKeys = {
  all: ["cf-message"] as const,
  templates: (pageId: string) => ["cf-message", "templates", pageId] as const,
  activeRate: () => ["cf-message", "rates", "active"] as const,
  rateHistory: () => ["cf-message", "rates", "history"] as const,
};

export function useTemplatesQuery(pageId: string | null) {
  return useQuery({
    queryKey: cfMessageKeys.templates(pageId ?? ""),
    queryFn: () => cfMessageApi.listTemplates(requirePageId(pageId)),
    enabled: Boolean(pageId),
  });
}

export function useUpsertTemplate(pageId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      kind: TemplateKind;
      content: string;
      isActive?: boolean;
    }) =>
      cfMessageApi.upsertTemplate(input.kind, {
        pageId: requirePageId(pageId),
        content: input.content,
        isActive: input.isActive,
      }),
    onSuccess: () => {
      if (pageId) {
        qc.invalidateQueries({ queryKey: cfMessageKeys.templates(pageId) });
      }
      toast.success("ບັນທຶກ Template ສຳເລັດ");
    },
    onError: () => toast.error("ບັນທຶກ Template ລົ້ມເຫຼວ"),
  });
}

export function useResetTemplate(pageId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (kind: TemplateKind) =>
      cfMessageApi.resetTemplate(kind, { pageId: requirePageId(pageId) }),
    onSuccess: () => {
      if (pageId) {
        qc.invalidateQueries({ queryKey: cfMessageKeys.templates(pageId) });
      }
      toast.success("ກັບເປັນຄ່າເລີ່ມຕົ້ນແລ້ວ");
    },
    onError: () => toast.error("ກັບເປັນຄ່າເລີ່ມຕົ້ນລົ້ມເຫຼວ"),
  });
}

export function useUpsertNotificationSettings(pageId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (enabledStatusEvents: CustomerStatusEventType[]) =>
      cfMessageApi.upsertNotificationSettings({
        pageId: requirePageId(pageId),
        enabledStatusEvents,
      }),
    onSuccess: () => {
      if (pageId) {
        qc.invalidateQueries({ queryKey: cfMessageKeys.templates(pageId) });
      }
      toast.success("ບັນທຶກການແຈ້ງສະຖານະແລ້ວ");
    },
    onError: () => toast.error("ບັນທຶກການແຈ້ງສະຖານະບໍ່ສຳເລັດ"),
  });
}

export function useUpsertUtilitySettings(pageId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      paymentReminderTemplateName?: string | null;
      orderStatusTemplateName?: string | null;
      deliveryStatusTemplateName?: string | null;
      deliveryArtifactTemplateName?: string | null;
      languageCode?: string;
      templatesReady: boolean;
    }) =>
      cfMessageApi.upsertUtilitySettings({
        pageId: requirePageId(pageId),
        ...input,
      }),
    onSuccess: () => {
      if (pageId) {
        qc.invalidateQueries({ queryKey: cfMessageKeys.templates(pageId) });
      }
      toast.success("ບັນທຶກ utility template ແລ້ວ");
    },
    onError: (err: unknown) => {
      const code =
        err && typeof err === "object" && "code" in err
          ? String((err as { code?: string }).code)
          : "";
      if (code === "UTILITY_TEMPLATE_NAME_REQUIRED") {
        toast.error("ຕ້ອງໃສ່ຊື່ Meta template ກ່ອນເປີດ ready");
        return;
      }
      if (code === "UTILITY_TEMPLATE_NOT_APPROVED") {
        toast.error("Meta ຍັງບໍ່ອະນຸມັດ template ນີ້ (ຕ້ອງເປັນ APPROVED)");
        return;
      }
      if (code === "UTILITY_TEMPLATE_NOT_FOUND_ON_PAGE") {
        toast.error("ບໍ່ພົບ template ນີ້ໃນ Page ບົນ Meta");
        return;
      }
      if (code === "UTILITY_TEMPLATE_VERIFICATION_FAILED") {
        toast.error("ກວດສອບ template ກັບ Meta ບໍ່ສຳເລັດ ຈຶ່ງຍັງເປີດສົ່ງບໍ່ໄດ້");
        return;
      }
      toast.error("ບັນທຶກ utility template ບໍ່ສຳເລັດ");
    },
  });
}

export function useUpsertStatusEventTemplate(pageId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      eventType: CustomerStatusEventType;
      content: string;
    }) =>
      cfMessageApi.upsertStatusEventTemplate(input.eventType, {
        pageId: requirePageId(pageId),
        content: input.content,
      }),
    onSuccess: () => {
      if (pageId) {
        qc.invalidateQueries({ queryKey: cfMessageKeys.templates(pageId) });
      }
      toast.success("ບັນທຶກຂໍ້ຄວາມສະຖານະແລ້ວ");
    },
    onError: () => toast.error("ບັນທຶກຂໍ້ຄວາມສະຖານະບໍ່ສຳເລັດ"),
  });
}

export function usePreviewTemplate() {
  return useMutation({
    mutationFn: (input: { kind: TemplateKind; content?: string }) =>
      cfMessageApi.preview(input),
  });
}

export function useSendReminder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (orderId: string) => cfMessageApi.sendReminder(orderId),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ["feeds"] });
      const r = res.result;
      if (r.status === "queued") {
        toast.message("ກຳລັງສົ່ງທວງຍອດ", {
          description: "ລໍຖ້າຜົນສົ່ງ — ສະຖານະຈະອັບເດດໃນຕາຕະລາງ",
        });
      } else if (r.status === "sent") {
        toast.success("ສົ່ງທວງຍອດສຳເລັດ");
      } else if (r.status === "failed") {
        toast.error("ສົ່ງທວງຍອດລົ້ມເຫຼວ", {
          description: reminderReasonDescription(r.errorCode),
        });
      } else {
        toast.message("ບໍ່ໄດ້ສົ່ງທວງຍອດ", {
          description: reminderReasonDescription(r.reason),
        });
      }
    },
    onError: () => toast.error("ສົ່ງທວງຍອດລົ້ມເຫຼວ"),
  });
}

export function useActiveRateQuery() {
  return useQuery({
    queryKey: cfMessageKeys.activeRate(),
    queryFn: () => cfMessageApi.activeRate(),
  });
}

export function useRateHistoryQuery() {
  return useQuery({
    queryKey: cfMessageKeys.rateHistory(),
    queryFn: () => cfMessageApi.rateHistory(50),
  });
}

export function useCreateRate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      rateKipToBaht: number;
      rateKipToUsd: number;
      notes?: string | null;
    }) => cfMessageApi.createRate(input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: cfMessageKeys.activeRate() });
      qc.invalidateQueries({ queryKey: cfMessageKeys.rateHistory() });
      toast.success("ບັນທຶກອັດຕາແລກປ່ຽນແລ້ວ");
    },
    onError: () => toast.error("ບັນທຶກອັດຕາແລ້ວປ່ຽນລົ້ມເຫຼວ"),
  });
}
